"use client";

import { blogService } from "@/lib/services";
import { BlogPost } from "@/lib/types";
import { Search } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

// Skeleton for Recent Posts
function RecentPostSkeleton() {
	return (
		<div className="flex gap-3 animate-pulse">
			<div className="relative w-24 h-20 flex-shrink-0 bg-gray-300 rounded" />
			<div className="flex-1">
				<div className="h-4 bg-gray-300 rounded mb-2" />
				<div className="h-3 bg-gray-300 rounded w-1/2" />
			</div>
		</div>
	);
}

export default function BlogSidebar() {
	const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		const fetchRecentPosts = async () => {
			setLoading(true);
			setError(false);

			try {
				const response = await blogService.getAll(1, 3);

				if (response.success && response.data) {
					setRecentPosts(response.data);
				} else {
					setError(true);
				}
			} catch (err) {
				console.error("Error fetching recent posts:", err);
				setError(true);
			} finally {
				setLoading(false);
			}
		};

		fetchRecentPosts();
	}, []);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			// TODO: Implement search functionality
			console.log("Searching for:", searchQuery);
		}
	};

	return (
		<aside className="space-y-8">
			{/* Search */}
			<form onSubmit={handleSearch} className="relative">
				<input
					type="text"
					placeholder="Search..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className="w-full px-6 py-3 pr-12 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
				/>
				<button
					type="submit"
					title="Search"
					className="absolute right-0 top-0 h-full px-4 bg-primary text-white rounded-r-lg hover:bg-blue-700 transition-colors"
				>
					<Search className="w-5 h-5" />
				</button>
			</form>

			{/* Recent Posts */}
			<div>
				<h3 className="text-lg font-bold text-gray-900 mb-4 uppercase">
					Recent Posts
				</h3>

				{/* Loading State */}
				{loading && (
					<div className="space-y-4">
						{[1, 2, 3].map((i) => (
							<RecentPostSkeleton key={i} />
						))}
					</div>
				)}

				{/* Error State */}
				{error && !loading && (
					<div className="text-sm text-gray-600">
						<p>Unable to load recent posts.</p>
					</div>
				)}

				{/* Empty State */}
				{!loading && !error && recentPosts.length === 0 && (
					<div className="text-sm text-gray-600">
						<p>No recent posts available.</p>
					</div>
				)}

				{/* Success State */}
				{!loading && !error && recentPosts.length > 0 && (
					<div className="space-y-4">
						{recentPosts.map((post) => (
							<Link
								key={post.id}
								href={`/blog/${post.id}`}
								className="flex gap-3 hover:opacity-75 transition-opacity"
							>
								<div className="relative w-24 h-20 flex-shrink-0">
									<Image
										src={post.featured_image || "/assets/black/1.jpg"}
										alt={post.title}
										fill
										className="object-cover rounded"
									/>
								</div>
								<div>
									<h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
										{post.title}
									</h4>
									<p className="text-xs text-gray-500">
										{post.published_at
											? new Date(post.published_at).toLocaleDateString(
													"en-US",
													{
														month: "short",
														day: "numeric",
														year: "numeric",
													}
											  )
											: "Date"}
									</p>
								</div>
							</Link>
						))}
					</div>
				)}
			</div>

			{/* Recent Comments */}
			<div>
				<h3 className="text-lg font-bold text-gray-900 mb-4 uppercase">
					Recent Comments
				</h3>
				<p className="text-sm text-gray-600">No comments to show</p>
			</div>
		</aside>
	);
}
